// src/components/OralExamSelection/OralExamSelection.jsx
import React, { useState, useEffect, useCallback } from 'react';
import styles from './OralExamSelection.module.css'; // Import CSS module
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophoneAlt, faClock, faDownload, faInfoCircle, faExclamationTriangle, faCheckCircle, faMicrophoneSlash, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Contexts/AuthContext';
import Menu from '../components/Navbar';
import Footer from '../components/Footer';


const getLastScore = async (taskType) => {
    // Mock API call to get last score.
    // In a real app, this would be an actual fetch or axios call to your backend.
    console.log(`Fetching last score for ${taskType}...`);
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay

    const mockScores = {
        task1: { cecrlLevel: 'B2', score: 14.5, date: '2025-05-20' },
        task2: { cecrlLevel: 'C1', score: 17.0, date: '2025-05-18' },
        task3: { cecrlLevel: 'B1', score: 12.0, date: '2025-05-15' },
        complete: { cecrlLevel: 'B2', score: 15.5, date: '2025-05-22' },
    };
    return mockScores[taskType] || null;
};

// Replace with a proper toast notification library (e.g., react-toastify, notistack)
const showToast = (message, type = 'info') => {
    alert(`${type.toUpperCase()}: ${message}`); // Simple alert for demonstration
};

// Oral task definitions remain the same
const oralTaskDefinitions = {
    task1: {
        title: "Entretien dirigé",
        instructions: "Interaction spontanée avec l'examinateur sur des sujets personnels",
        duration: "2 min",
        expectedDescription: "Sans préparation",
        themes: "Situation civile, personnalité, langues, famille, parcours"
    },
    task2: {
        title: "Exercice interactif",
        instructions: "Recueillir des informations dans une situation de la vie quotidienne",
        duration: "5 min 30",
        expectedDescription: "2 min préparation + 3 min 30 échange",
        themes: "Situations pratiques, demandes d'information"
    },
    task3: {
        title: "Expression d'un point de vue",
        instructions: "Faire preuve de sa capacité à parler de manière spontanée et convaincante",
        duration: "4 min 30",
        expectedDescription: "Sans préparation",
        themes: "Questions spontanées, expression d'opinions"
    }
};

const OralExamSelection = () => {
    const navigation = useNavigate();
    const { user } = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [canSubmit, setCanSubmit] = useState(true); // Default to true for web demo
    const [remainingSubmissions, setRemainingSubmissions] = useState(3); // Default to 3 for web demo
    const [microphonePermission, setMicrophonePermission] = useState(null); // 'granted', 'denied', null

    const [sessionData, setSessionData] = useState({});
    const [loadingSessionType, setLoadingSessionType] = useState(null);
    const [sessionLoadingStep, setSessionLoadingStep] = useState('');
    const [sessionErrors, setSessionErrors] = useState({});

    const [lastScores, setLastScores] = useState({
        task1: null,
        task2: null,
        task3: null,
        complete: null
    });

    // Determine level color for score badges
    const getLevelColor = (level) => {
        switch (level) {
            case 'C2': return '#4caf50'; // vert
            case 'C1': return '#2196f3'; // bleu
            case 'B2': return '#03a9f4'; // bleu clair
            case 'B1': return '#ffc107'; // jaune
            case 'A2': return '#ff9800'; // orange
            case 'A1': return '#f44336'; // rouge
            default: return '#9e9e9e'; // gris
        }
    };

    // Check microphone permission
    useEffect(() => {
        const checkMicrophonePermission = async () => {
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
                    setMicrophonePermission('granted');
                } catch (error) {
                    console.error('Error checking microphone permission:', error);
                    setMicrophonePermission('denied');
                }
            } else {
                setMicrophonePermission('denied'); // MediaDevices not supported
                showToast("Votre navigateur ne supporte pas l'accès au microphone.", 'error');
            }
        };

        checkMicrophonePermission();
    }, []);

    // Request microphone permission
    const requestMicrophonePermission = useCallback(async () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                stream.getTracks().forEach(track => track.stop());
                setMicrophonePermission('granted');
                showToast("Accès au microphone autorisé !", 'success');
            } catch (error) {
                console.error('Error requesting microphone permission:', error);
                setMicrophonePermission('denied');
                showToast("L'accès au microphone est nécessaire pour enregistrer votre expression orale. Veuillez autoriser l'accès dans les paramètres de votre navigateur.", 'error');
            }
        } else {
            setMicrophonePermission('denied');
            showToast("Votre navigateur ne supporte pas l'accès au microphone.", 'error');
        }
    }, []);

    // Check user eligibility (mock API call)
    useEffect(() => {
        const checkSubmissionEligibility = async () => {
            try {
                setIsLoading(true);
                // Simulate API call for eligibility
                await new Promise(resolve => setTimeout(resolve, 1000));
                // Mock result
                setCanSubmit(true);
                setRemainingSubmissions(3); // Example: 3 remaining submissions
                // setCanSubmit(false); setRemainingSubmissions(0); // For testing "no exams" state
            } catch (error) {
                console.error('Error checking eligibility:', error);
                showToast("Une erreur s'est produite lors de la vérification de votre éligibilité.", 'error');
            } finally {
                setIsLoading(false);
            }
        };

        if (user && user.uid) {
            checkSubmissionEligibility();
        } else {
            setIsLoading(false);
            setCanSubmit(false); // No user, cannot submit
            setRemainingSubmissions(0);
        }
    }, [user]);

    // Fetch last scores
    useEffect(() => {
        const fetchLastScores = async () => {
            try {
                const scores = {};
                for (const task of ['task1', 'task2', 'task3', 'complete']) {
                    scores[task] = await getLastScore(task);
                }
                setLastScores(scores);
            } catch (error) {
                console.error('Error fetching scores:', error);
                showToast("Impossible de récupérer les derniers scores.", 'error');
            }
        };

        fetchLastScores();
    }, []);

    const fetchSessionData = useCallback(async (examType) => {
        try {
            setLoadingSessionType(examType);
            setSessionLoadingStep('Préparation de votre session...');
            setSessionErrors(prev => ({ ...prev, [examType]: null }));

            const getApiParams = (type) => {
                switch (type) {
                    case 'complete': return { typeExercice: "complet", criteria: [{ questionNumber: 1, subjectName: "EO" }, { questionNumber: 2, subjectName: "EO" }, { questionNumber: 3, subjectName: "EO" }], vitesse: 0.5 };
                    case 'task1': return { typeExercice: "tache1", criteria: [{ questionNumber: 1, subjectName: "EO" }], vitesse: 0.5 };
                    case 'task2': return { typeExercice: "tache2", criteria: [{ questionNumber: 2, subjectName: "EO" }], vitesse: 0.5 };
                    case 'task3': return { typeExercice: "tache3", criteria: [{ questionNumber: 3, subjectName: "EO" }], vitesse: 0.5 };
                    default: throw new Error(`Type d'examen non supporté: ${type}`);
                }
            };

            const apiParams = getApiParams(examType);

            setSessionLoadingStep('Génération des sujets personnalisés...');

            const response = await fetch('https://fwabl598k2.execute-api.us-west-1.amazonaws.com/dev/tcfagenteo/generate-complete-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiParams)
            });

            if (!response.ok) {
                throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
            }

            setSessionLoadingStep('Traitement des données...');
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || 'Erreur lors de la génération de la session');
            }

            setSessionLoadingStep('Finalisation...');
            // Stocker les données de session pour ce type d'examen
            setSessionData(prev => ({
                ...prev,
                [examType]: data.data
            }));

            // Petit délai pour montrer la finalisation
            await new Promise(resolve => setTimeout(resolve, 500));

            return data.data;

        } catch (error) {
            console.error(`Erreur lors du chargement de la session ${examType}:`, error);
            setSessionErrors(prev => ({
                ...prev,
                [examType]: error.message
            }));
            showToast(`Impossible de charger les sujets pour l'${examType === 'complete' ? 'examen complet' : `exercice ${examType}`}. Veuillez réessayer.`, 'error');
            throw error; // Propagate error to prevent navigation
        } finally {
            setLoadingSessionType(null);
            setSessionLoadingStep('');
        }
    }, []);

    const handleExamStart = async (examType) => {
        if (!canSubmit || remainingSubmissions <= 0) {
            showToast("Vous n'avez plus d'examens disponibles. Veuillez souscrire à un abonnement pour continuer.", 'warning');
            return;
        }

        if (microphonePermission !== 'granted') {
            showToast("L'accès au microphone est nécessaire pour cet examen. Veuillez autoriser l'accès.", 'warning');
            // Offer to request permission
            requestMicrophonePermission();
            return;
        }

        if (!sessionData[examType]) {
            try {
                const loadedSession = await fetchSessionData(examType);
                navigation.navigate('examen/ExamScreenExpressionOrale', {
                    type: examType,
                    sessionData: loadedSession
                });
            } catch (error) {
                // Error already handled by fetchSessionData's toast
            }
        } else {
            // Data already loaded, navigate directly
            navigation.navigate('examen/ExamScreenExpressionOrale', {
                type: examType,
                sessionData: sessionData[examType]
            });
        }
    };

    const getSessionStatus = (examType) => {
        if (loadingSessionType === examType) return 'loading';
        if (sessionErrors[examType]) return 'error';
        if (sessionData[examType]) return 'loaded';
        return 'idle';
    };

    const SessionStatusBadge = ({ examType }) => {
        const status = getSessionStatus(examType);

        switch (status) {
            case 'loading':
                return (
                    <div className={styles['oral-exam-session-badge']} data-status="loading">
                        <FontAwesomeIcon icon={faInfoCircle} className={styles['oral-exam-icon-spin']} />
                        <span className={styles['oral-exam-session-badge-text']}>Chargement...</span>
                    </div>
                );
            case 'loaded':
                const voix = sessionData[examType]?.metadata?.voixUtilisee;
                return (
                    <div className={styles['oral-exam-session-badge']} data-status="loaded">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span className={styles['oral-exam-session-badge-text']}>Prêt {voix ? `• ${voix}` : ''}</span>
                    </div>
                );
            case 'error':
                return (
                    <div className={styles['oral-exam-session-badge']} data-status="error">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className={styles['oral-exam-session-badge-text']}>Erreur</span>
                    </div>
                );
            default:
                return null;
        }
    };


    if (isLoading) {
        return (
            <div className={styles['oral-exam-loading-container']}>
                <div className={styles['oral-exam-loader']}></div>
                <p className={styles['oral-exam-loading-text']}>Vérification de votre abonnement...</p>
            </div>
        );
    }

    const getRemainingBadgeColor = () => {
        if (remainingSubmissions <= 0) return styles['oral-exam-badge-red'];
        if (remainingSubmissions <= 2) return styles['oral-exam-badge-orange'];
        return styles['oral-exam-badge-default']; // Default TCF red
    };

    return (
        <>
            <Menu />
            <div style={{ marginTop: '5rem' }}></div>
            <div className={styles['oral-exam-container']}>
                <header className={styles['oral-exam-header-container']}>
                    <div className={styles['oral-exam-header-content']}>
                        <div className={styles['oral-exam-logo-section']}>
                            <h1 className={styles['oral-exam-logo-text']}>TCF Canada</h1>
                            <p className={styles['oral-exam-subtitle-text']}>Expression orale</p>
                        </div>

                        <div className={styles['oral-exam-remaining-container']}>
                            <div className={`${styles['oral-exam-remaining-badge']} ${getRemainingBadgeColor()}`}>
                                <FontAwesomeIcon icon={faMicrophoneAlt} />
                                <span className={styles['oral-exam-remaining-number']}>{remainingSubmissions}</span>
                            </div>
                            <p className={styles['oral-exam-remaining-text']}>Examens restants</p>
                        </div>
                    </div>

                    <div className={styles['oral-exam-welcome-container']}>
                        <p className={styles['oral-exam-welcome-text']}>
                            Ce module vous permet de vous entraîner aux différentes tâches d'expression orale
                            du TCF Canada en simulant les conditions réelles de l'examen avec enregistrement audio.
                        </p>
                    </div>
                </header>

                <main className={styles['oral-exam-main-content']}>
                    <h2 className={styles['oral-exam-section-title']}>Choisissez votre exercice</h2>

                    <div className={styles['oral-exam-cards-grid']}>
                        {/* Exam complet */}
                        <button
                            className={`${styles['oral-exam-card']} ${(!canSubmit || remainingSubmissions <= 0) ? styles['oral-exam-card-disabled'] : ''}`}
                            onClick={() => handleExamStart('complete')}
                            disabled={!canSubmit || remainingSubmissions <= 0}
                        >
                            {lastScores.complete && (
                                <div className={styles['oral-exam-score-badge']} style={{ backgroundColor: getLevelColor(lastScores.complete.cecrlLevel) }}>
                                    <span className={styles['oral-exam-score-badge-text']}>
                                        {lastScores.complete.cecrlLevel}: {lastScores.complete.score}/20
                                    </span>
                                </div>
                            )}
                            <SessionStatusBadge examType="complete" />
                            <div className={styles['oral-exam-card-icon']}>
                                <FontAwesomeIcon icon={faMicrophoneAlt} className={styles['oral-exam-card-icon-main']} />
                            </div>
                            <h3 className={styles['oral-exam-card-title']}>Examen complet</h3>
                            <p className={styles['oral-exam-card-description']}>
                                Simulez l'examen complet avec les trois tâches d'expression orale, comme dans
                                les conditions réelles du TCF Canada avec enregistrement.
                            </p>
                            <div className={styles['oral-exam-card-footer']}>
                                <div className={styles['oral-exam-time-info']}>
                                    <FontAwesomeIcon icon={faClock} />
                                    <span className={styles['oral-exam-time-text']}>12 minutes</span>
                                </div>
                                <span className={styles['oral-exam-task-count-text']}>3 tâches</span>
                            </div>
                        </button>

                        {/* Tâche 1 */}
                        <button
                            className={`${styles['oral-exam-card']} ${(!canSubmit || remainingSubmissions <= 0) ? styles['oral-exam-card-disabled'] : ''}`}
                            onClick={() => handleExamStart('task1')}
                            disabled={!canSubmit || remainingSubmissions <= 0}
                        >
                            {lastScores.task1 && (
                                <div className={styles['oral-exam-score-badge']} style={{ backgroundColor: getLevelColor(lastScores.task1.cecrlLevel) }}>
                                    <span className={styles['oral-exam-score-badge-text']}>
                                        {lastScores.task1.cecrlLevel}: {lastScores.task1.score}/20
                                    </span>
                                </div>
                            )}
                            <SessionStatusBadge examType="task1" />
                            <div className={styles['oral-exam-card-icon']}>
                                <span role="img" aria-label="waving hand" className={styles['oral-exam-card-emoji-icon']}>👋</span>
                            </div>
                            <h3 className={styles['oral-exam-card-title']}>Tâche 1 - {oralTaskDefinitions.task1.title}</h3>
                            <p className={styles['oral-exam-card-description']}>
                                {oralTaskDefinitions.task1.instructions}
                            </p>
                            <p className={styles['oral-exam-card-themes']}>
                                Thèmes : {oralTaskDefinitions.task1.themes}
                            </p>
                            <div className={styles['oral-exam-card-footer']}>
                                <div className={styles['oral-exam-time-info']}>
                                    <FontAwesomeIcon icon={faClock} />
                                    <span className={styles['oral-exam-time-text']}>{oralTaskDefinitions.task1.duration}</span>
                                </div>
                            </div>
                            <div className={styles['oral-exam-card-details']}>
                                <p className={styles['oral-exam-task-details-text']}>{oralTaskDefinitions.task1.expectedDescription}</p>
                            </div>
                        </button>

                        {/* Tâche 2 */}
                        <button
                            className={`${styles['oral-exam-card']} ${(!canSubmit || remainingSubmissions <= 0) ? styles['oral-exam-card-disabled'] : ''}`}
                            onClick={() => handleExamStart('task2')}
                            disabled={!canSubmit || remainingSubmissions <= 0}
                        >
                            {lastScores.task2 && (
                                <div className={styles['oral-exam-score-badge']} style={{ backgroundColor: getLevelColor(lastScores.task2.cecrlLevel) }}>
                                    <span className={styles['oral-exam-score-badge-text']}>
                                        {lastScores.task2.cecrlLevel}: {lastScores.task2.score}/20
                                    </span>
                                </div>
                            )}
                            <SessionStatusBadge examType="task2" />
                            <div className={styles['oral-exam-card-icon']}>
                                <span role="img" aria-label="speaking head" className={styles['oral-exam-card-emoji-icon']}>🗣️</span>
                            </div>
                            <h3 className={styles['oral-exam-card-title']}>Tâche 2 - {oralTaskDefinitions.task2.title}</h3>
                            <p className={styles['oral-exam-card-description']}>
                                {oralTaskDefinitions.task2.instructions}
                            </p>
                            <p className={styles['oral-exam-card-themes']}>
                                Thèmes : {oralTaskDefinitions.task2.themes}
                            </p>
                            <div className={styles['oral-exam-card-footer']}>
                                <div className={styles['oral-exam-time-info']}>
                                    <FontAwesomeIcon icon={faClock} />
                                    <span className={styles['oral-exam-time-text']}>{oralTaskDefinitions.task2.duration}</span>
                                </div>
                            </div>
                            <div className={styles['oral-exam-card-details']}>
                                <p className={styles['oral-exam-task-details-text']}>{oralTaskDefinitions.task2.expectedDescription}</p>
                            </div>
                        </button>

                        {/* Tâche 3 */}
                        <button
                            className={`${styles['oral-exam-card']} ${(!canSubmit || remainingSubmissions <= 0) ? styles['oral-exam-card-disabled'] : ''}`}
                            onClick={() => handleExamStart('task3')}
                            disabled={!canSubmit || remainingSubmissions <= 0}
                        >
                            {lastScores.task3 && (
                                <div className={styles['oral-exam-score-badge']} style={{ backgroundColor: getLevelColor(lastScores.task3.cecrlLevel) }}>
                                    <span className={styles['oral-exam-score-badge-text']}>
                                        {lastScores.task3.cecrlLevel}: {lastScores.task3.score}/20
                                    </span>
                                </div>
                            )}
                            <SessionStatusBadge examType="task3" />
                            <div className={styles['oral-exam-card-icon']}>
                                <span role="img" aria-label="thought balloon" className={styles['oral-exam-card-emoji-icon']}>💭</span>
                            </div>
                            <h3 className={styles['oral-exam-card-title']}>Tâche 3 - {oralTaskDefinitions.task3.title}</h3>
                            <p className={styles['oral-exam-card-description']}>
                                {oralTaskDefinitions.task3.instructions}
                            </p>
                            <p className={styles['oral-exam-card-themes']}>
                                Thèmes : {oralTaskDefinitions.task3.themes}
                            </p>
                            <div className={styles['oral-exam-card-footer']}>
                                <div className={styles['oral-exam-time-info']}>
                                    <FontAwesomeIcon icon={faClock} />
                                    <span className={styles['oral-exam-time-text']}>{oralTaskDefinitions.task3.duration}</span>
                                </div>
                            </div>
                            <div className={styles['oral-exam-card-details']}>
                                <p className={styles['oral-exam-task-details-text']}>{oralTaskDefinitions.task3.expectedDescription}</p>
                            </div>
                        </button>
                    </div>

                    {canSubmit && remainingSubmissions > 0 ? (
                        <div className={styles['oral-exam-info-section']}>


                            {microphonePermission === 'denied' && (
                                <button
                                    className={styles['oral-exam-permission-card']}
                                    onClick={requestMicrophonePermission}
                                >
                                    <FontAwesomeIcon icon={faMicrophoneSlash} className={styles['oral-exam-permission-icon']} />
                                    <div className={styles['oral-exam-permission-text-container']}>
                                        <h4 className={styles['oral-exam-permission-title']}>Autorisation microphone requise</h4>
                                        <p className={styles['oral-exam-permission-text']}>
                                            Cliquez ici pour autoriser l'accès au microphone
                                        </p>
                                    </div>
                                    <FontAwesomeIcon icon={faArrowRight} className={styles['oral-exam-arrow-icon']} />
                                </button>
                            )}

                            {microphonePermission === 'granted' && (
                                <div className={styles['oral-exam-info-card']}>
                                    <FontAwesomeIcon icon={faMicrophoneAlt} className={styles['oral-exam-info-icon-success']} />
                                    <p className={styles['oral-exam-info-text-success']}>
                                        Microphone autorisé - Vous êtes prêt à passer l'examen !
                                    </p>
                                </div>
                            )}

                            {microphonePermission === null && (
                                <div className={styles['oral-exam-info-card']}>
                                    <FontAwesomeIcon icon={faInfoCircle} className={styles['oral-exam-info-icon-info']} />
                                    <p className={styles['oral-exam-info-text-info']}>
                                        Vérification des permissions en cours...
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles['oral-exam-subscription-prompt']}>
                            <p className={styles['oral-exam-no-exams-text']}>
                                Vous n'avez plus d'examens disponibles.
                            </p>
                            <button
                                className={styles['oral-exam-subscription-button']}
                                onClick={() => navigation.navigate('subscription')}
                            >
                                Souscrire à un abonnement
                            </button>
                        </div>
                    )}
                </main>

                {/* Loading Modal for session generation */}
                {loadingSessionType && (
                    <div className={styles['oral-exam-modal-overlay']}>
                        <div className={styles['oral-exam-modal-content']}>
                            <div className={styles['oral-exam-loader-large']}></div>
                            <h3 className={styles['oral-exam-modal-title']}>Chargement des sujets</h3>
                            <p className={styles['oral-exam-modal-text']}>
                                Nous préparons vos sujets d'expression orale personnalisés.
                                Cela peut prendre quelques instants.
                            </p>
                            {sessionLoadingStep && (
                                <p className={styles['oral-exam-modal-step']}>{sessionLoadingStep}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

export default OralExamSelection;